import React, { useState, useEffect, useRef, useMemo } from 'react';
import { getSignedMediaUrl, formatCurrencyForDisplay } from '@/lib/utils';
import { motion, AnimatePresence } from 'motion/react';

interface MediaItem {
  id: string;
  url: string;
  type: 'image' | 'video';
}

interface BookingCategoryHeroProps {
  eventBookingCategory: {
    booking_category: {
      name: string;
      description: string;
      card_image_id: string | null;
      base_image_id?: string | null;
      base_video_id?: string | null;
      media?: { media_id: string; media_type: string; rank: number }[];
      business?: {
        handle: string;
      } | null;
    };
    price: number;
  };
  /** Currency code for price formatting (e.g., 'GBP', 'USD') */
  currency?: string;
  /** Whether this is a table booking to determine "per seat" vs "per ticket" */
  isTableBooking?: boolean;
}

const IMAGE_DURATION_MS = 3000;

const BookingCategoryHero: React.FC<BookingCategoryHeroProps> = ({
  eventBookingCategory,
  currency,
  isTableBooking = false
}) => {
  const { booking_category, price } = eventBookingCategory;
  const { name, description, card_image_id, base_image_id, base_video_id, media } = booking_category;

  // Format price
  const formatPrice = (amount: number) => currency 
    ? formatCurrencyForDisplay(amount, currency) 
    : `${(amount / 100).toFixed(2)}`;

  // Compile media items matching mobile logic
  const mobileMediaItems = useMemo(() => {
    const items: MediaItem[] = [];

    // Base media first
    if (base_video_id && base_video_id !== '00000000-0000-0000-0000-000000000000') {
      items.push({ id: base_video_id, url: getSignedMediaUrl(base_video_id, 'video').url, type: 'video' });
    } else if (base_image_id && base_image_id !== '00000000-0000-0000-0000-000000000000') {
      items.push({ id: base_image_id, url: getSignedMediaUrl(base_image_id, 'image').url, type: 'image' });
    } else if (card_image_id && card_image_id !== '00000000-0000-0000-0000-000000000000') {
      items.push({ id: card_image_id, url: getSignedMediaUrl(card_image_id, 'image').url, type: 'image' });
    }

    // Then other media, sorted by rank
    if (media && media.length > 0) {
      const sortedMedia = [...media].sort((a, b) => a.rank - b.rank);
      sortedMedia.forEach(m => {
        if (m.media_id === base_image_id || m.media_id === card_image_id || m.media_id === base_video_id) return;
        const type = (m.media_type || '').toLowerCase().startsWith('video') ? 'video' : 'image';
        items.push({ id: m.media_id, url: getSignedMediaUrl(m.media_id, type).url, type });
      });
    }

    // Fallback if none found
    if (items.length === 0) {
      items.push({ id: 'fallback', url: '', type: 'image' });
    }

    return items;
  }, [base_image_id, card_image_id, base_video_id, media]);

  // Compile media items for desktop (excluding base image/video)
  const desktopMediaItems = useMemo(() => {
    const items: MediaItem[] = [];

    // Then other media, sorted by rank
    if (media && media.length > 0) {
      const sortedMedia = [...media].sort((a, b) => a.rank - b.rank);
      sortedMedia.forEach(m => {
        // Skip base image/video for desktop
        if (m.media_id === base_image_id || m.media_id === base_video_id) return;
        
        // Still deduplicate card_image_id just in case
        if (m.media_id === card_image_id) return;
        
        const type = (m.media_type || '').toLowerCase().startsWith('video') ? 'video' : 'image';
        items.push({ id: m.media_id, url: getSignedMediaUrl(m.media_id, type).url, type });
      });
    }

    // Fallback to card_image_id if no media items found
    if (items.length === 0 && card_image_id && card_image_id !== '00000000-0000-0000-0000-000000000000') {
      items.push({ id: card_image_id, url: getSignedMediaUrl(card_image_id, 'image').url, type: 'image' });
    }

    // Final Fallback if absolutely nothing found
    if (items.length === 0) {
      items.push({ id: 'fallback', url: '', type: 'image' });
    }

    return items;
  }, [base_image_id, card_image_id, base_video_id, media]);

  // Need separate states for mobile/desktop since they have different arrays now
  const [mobileCurrentIndex, setMobileCurrentIndex] = useState(0);
  const [desktopCurrentIndex, setDesktopCurrentIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const mobileVideoRef = useRef<HTMLVideoElement>(null);
  const desktopVideoRef = useRef<HTMLVideoElement>(null);
  const animationRef = useRef<number>(0);

  // Desktop image background
  const desktopBaseImageUrl = useMemo(() => {
    if (base_image_id && base_image_id !== '00000000-0000-0000-0000-000000000000') {
      return getSignedMediaUrl(base_image_id, 'image').url;
    } else if (card_image_id && card_image_id !== '00000000-0000-0000-0000-000000000000') {
      return getSignedMediaUrl(card_image_id, 'image').url;
    }
    return '';
  }, [base_image_id, card_image_id]);

  const desktopBgStyle = desktopBaseImageUrl 
    ? { backgroundImage: `url(${desktopBaseImageUrl})` } 
    : { background: 'linear-gradient(135deg, #1f2937 0%, #374151 100%)' };

  const mobileActiveItem = mobileMediaItems[mobileCurrentIndex] || mobileMediaItems[0];
  const desktopActiveItem = desktopMediaItems[desktopCurrentIndex] || desktopMediaItems[0];
  const isVideo = mobileActiveItem?.type === 'video' || desktopActiveItem?.type === 'video';

  const advanceMobileNext = () => {
    setMobileCurrentIndex(prev => (prev + 1) % mobileMediaItems.length);
    setProgress(0);
  };

  const skipMobilePrevious = () => {
    setMobileCurrentIndex(prev => (prev - 1 + mobileMediaItems.length) % mobileMediaItems.length);
    setProgress(0);
  };
  // @ts-expect-error unused function kept for future manual mobile navigation
  const _unusedSkipMobile = skipMobilePrevious;

  const advanceDesktopNext = () => {
    setDesktopCurrentIndex(prev => (prev + 1) % desktopMediaItems.length);
    setProgress(0);
  };

  const skipDesktopPrevious = () => {
    setDesktopCurrentIndex(prev => (prev - 1 + desktopMediaItems.length) % desktopMediaItems.length);
    setProgress(0);
  };

  // Image auto-advance
  useEffect(() => {
    if (isVideo) return;
    
    // Check if we should animate mobile
    const animateMobile = mobileActiveItem?.id !== 'fallback' && mobileMediaItems.length > 1;
    const animateDesktop = desktopActiveItem?.id !== 'fallback' && desktopMediaItems.length > 1;
    
    if (!animateMobile && !animateDesktop) return;

    let start: number | null = null;
    
    const animate = (timestamp: number) => {
      if (!start) start = timestamp;
      const elapsed = timestamp - start;
      setProgress(Math.min((elapsed / IMAGE_DURATION_MS) * 100, 100));

      if (elapsed >= IMAGE_DURATION_MS) {
        if (animateMobile) advanceMobileNext();
        if (animateDesktop) advanceDesktopNext();
      } else {
        animationRef.current = requestAnimationFrame(animate);
      }
    };

    setProgress(0);
    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [mobileCurrentIndex, desktopCurrentIndex, isVideo, mobileActiveItem?.id, desktopActiveItem?.id, mobileMediaItems.length, desktopMediaItems.length]);

  // Video init
  useEffect(() => {
    if (!isVideo) return;
    setProgress(0);
    
    const playVideo = (ref: React.RefObject<HTMLVideoElement | null>) => {
      if (ref.current) {
        ref.current.currentTime = 0;
        const playPromise = ref.current.play();
        if (playPromise !== undefined) {
          playPromise.catch(error => {
            console.error("Video auto-play failed", error);
          });
        }
      }
    };

    playVideo(mobileVideoRef);
    playVideo(desktopVideoRef);
  }, [mobileCurrentIndex, desktopCurrentIndex, isVideo]);

  const handleTimeUpdate = (e: React.SyntheticEvent<HTMLVideoElement>) => {
    const target = e.target as HTMLVideoElement;
    if (target) {
      const current = target.currentTime;
      const duration = target.duration || 1;
      setProgress((current / duration) * 100);
    }
  };

  return (
    <div className="w-full relative border-b border-white/5 bg-[#050505]">
      {/* --- MOBILE VIEW --- */}
      <div className="md:hidden flex justify-center pb-8 w-full">
        {/* Full width container spanning screen */}
        <div className="relative w-full h-[50vh] min-h-[400px] max-h-[600px] overflow-hidden group">
          
          {/* Background Media Container */}
          <div className="absolute inset-0">
            <AnimatePresence initial={false} mode="popLayout">
              <motion.div
                key={mobileActiveItem?.id}
                initial={{ opacity: 0, scale: 1.05 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.6, ease: 'easeOut' }}
                className="w-full h-full"
              >
                {mobileActiveItem?.id === 'fallback' ? (
                  <div className="w-full h-full" style={{ background: 'linear-gradient(135deg, #1f2937 0%, #374151 100%)' }} />
                ) : mobileActiveItem?.type === 'video' ? (
                  <video
                    ref={mobileVideoRef}
                    src={mobileActiveItem.url}
                    className="w-full h-full object-cover"
                    autoPlay
                    muted
                    playsInline
                    onTimeUpdate={handleTimeUpdate}
                    onEnded={advanceMobileNext}
                    onError={() => advanceMobileNext()}
                  />
                ) : (
                  <div 
                    className="w-full h-full"
                    style={{
                      backgroundImage: `url(${mobileActiveItem?.url})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center'
                    }}
                  />
                )}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Desktop Gradient Mask */}
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent pointer-events-none z-0" />
          <div className="absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-black via-black/40 to-transparent pointer-events-none z-0" />

          {/* Footer Content */}
          <div className="absolute z-20 inset-x-0 bottom-0 pb-8 flex flex-col justify-end pointer-events-none">
            <div className="w-full mx-auto px-6">
              <div className="flex justify-between items-end gap-6 w-full">
                <div className="flex-1 max-w-2xl">
                  <h2 className="text-4xl font-serif font-bold text-white leading-tight drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)] pb-3">
                    {name}
                  </h2>
                  <p className="text-base font-medium text-gray-200 drop-shadow-md line-clamp-2 leading-relaxed opacity-95">
                    {description}
                  </p>
                </div>

                <div className="flex flex-col items-end flex-shrink-0 mb-1">
                  <span className="text-4xl font-black text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
                    {formatPrice(price || 0)}
                  </span>
                  <span className="text-xs font-bold tracking-widest uppercase text-gray-300/90 drop-shadow-sm mt-1.5 bg-black/40 px-3 py-1 rounded-full backdrop-blur-md border border-white/10">
                    {isTableBooking ? 'per seat' : 'per ticket'}
                  </span>
                </div>
              </div>

              {/* Progress Indicators */}
              {mobileMediaItems.length > 1 && (
                <div className="flex justify-start gap-2 w-full mt-6 items-center">
                  {mobileMediaItems.map((_, idx) => {
                    const isActive = idx === mobileCurrentIndex;
                    const isPast = idx < mobileCurrentIndex;
                    return (
                      <motion.div
                        key={idx}
                        className="h-1.5 rounded-full overflow-hidden bg-white/20 backdrop-blur-md"
                        initial={false}
                        animate={{
                          width: isActive ? 32 : 12,
                          opacity: isActive ? 1 : isPast ? 0.8 : 0.5
                        }}
                        transition={{ duration: 0.3 }}
                      >
                        {isActive && (
                          <motion.div
                            className="h-full bg-white shadow-[0_0_10px_rgba(255,255,255,0.8)]"
                            style={{ width: `${progress}%` }}
                            transition={{ duration: 0.1, ease: "linear" }}
                          />
                        )}
                        {isPast && (
                          <div className="w-full h-full bg-white shadow-[0_0_10px_rgba(255,255,255,0.8)]" />
                        )}
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          
        </div>
      </div>

      {/* --- DESKTOP VIEW --- */}
      <div className="hidden md:flex relative w-full overflow-hidden h-full md:min-h-[350px] lg:min-h-[400px] 2xl:min-h-[600px] items-center py-6 lg:py-10 2xl:py-24 shrink-0">
        {/* Base Image as Background */}
        <div className="absolute inset-0 z-0">
          <div 
            className="absolute inset-0 bg-cover bg-center opacity-80"
            style={desktopBgStyle}
          />
          <div className="absolute inset-0 bg-black/70 z-0" />
        </div>

        {/* Vertical gradients for blending into the black backgrounds */}
        <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-[#050505] to-transparent pointer-events-none z-0" />
        <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-black to-transparent pointer-events-none z-0" />

        {/* Desktop Content Grid */}
        <div className="relative z-10 w-full mx-auto px-6 md:px-8 lg:px-12 xl:px-20 2xl:px-[12vw]">
          <div className="grid grid-cols-2 lg:grid-cols-[1.2fr_0.8fr] xl:grid-cols-[1.1fr_0.9fr] 2xl:grid-cols-2 gap-6 md:gap-8 lg:gap-12 xl:gap-16 items-center">
            
            {/* Left Column: Details */}
            <div className="flex flex-col justify-center space-y-3 lg:space-y-4 xl:space-y-5 2xl:space-y-8">
              <h2 className="text-2xl lg:text-3xl xl:text-4xl 2xl:text-6xl font-serif font-bold text-white leading-tight drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)] pb-1">
                {name}
              </h2>
              
              <p className="text-sm lg:text-sm xl:text-base 2xl:text-2xl font-medium text-gray-200 drop-shadow-md line-clamp-3 2xl:line-clamp-4 leading-relaxed opacity-95">
                {description}
              </p>

              <div className="flex flex-col items-start pt-1 lg:pt-2 2xl:pt-6">
                <span className="text-2xl lg:text-3xl xl:text-4xl 2xl:text-6xl font-black text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
                  {formatPrice(price || 0)}
                </span>
                <span className="text-[10px] lg:text-xs 2xl:text-lg font-bold tracking-widest uppercase text-gray-300/90 drop-shadow-sm mt-1.5 2xl:mt-3 bg-black/40 px-2.5 py-0.5 lg:px-3 lg:py-1 2xl:px-5 2xl:py-2 rounded-full backdrop-blur-md border border-white/10">
                  {isTableBooking ? 'per seat' : 'per ticket'}
                </span>
              </div>
            </div>

            {/* Right Column: Media Carousel */}
            <div className="relative w-full max-w-lg lg:max-w-2xl 2xl:max-w-4xl lg:ml-auto">
              {/* Aspect Ratio Container */}
              <div className="relative w-full aspect-[4/3] rounded-xl lg:rounded-2xl 2xl:rounded-[32px] overflow-hidden shadow-2xl ring-1 ring-white/10 group bg-black/50">
                <AnimatePresence initial={false} mode="popLayout">
                  <motion.div
                    key={desktopActiveItem?.id}
                    initial={{ opacity: 0, scale: 1.05 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.6, ease: 'easeOut' }}
                    className="w-full h-full"
                  >
                    {desktopActiveItem?.id === 'fallback' ? (
                      <div className="w-full h-full" style={{ background: 'linear-gradient(135deg, #1f2937 0%, #374151 100%)' }} />
                    ) : desktopActiveItem?.type === 'video' ? (
                      <video
                        ref={desktopVideoRef}
                        src={desktopActiveItem.url}
                        className="w-full h-full object-cover"
                        autoPlay
                        muted
                        playsInline
                        onTimeUpdate={handleTimeUpdate}
                        onEnded={advanceDesktopNext}
                        onError={() => advanceDesktopNext()}
                      />
                    ) : (
                      <div 
                        className="w-full h-full"
                        style={{
                          backgroundImage: `url(${desktopActiveItem?.url})`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center'
                        }}
                      />
                    )}
                  </motion.div>
                </AnimatePresence>

                {/* Hover Navigation Arrows */}
                {desktopMediaItems.length > 1 && (
                  <div className="absolute inset-0 z-10 w-full px-3 lg:px-4 flex items-center justify-between opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                    <button 
                      className="w-8 h-8 lg:w-10 lg:h-10 2xl:w-14 2xl:h-14 pointer-events-auto rounded-full bg-black/50 hover:bg-black/80 backdrop-blur-md flex items-center justify-center text-white transition-all hover:scale-110 active:scale-95 border border-white/20"
                      onClick={skipDesktopPrevious}
                    >
                      <svg className="w-4 h-4 lg:w-5 lg:h-5 2xl:w-8 2xl:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                    </button>
                    <button 
                      className="w-8 h-8 lg:w-10 lg:h-10 2xl:w-14 2xl:h-14 pointer-events-auto rounded-full bg-black/50 hover:bg-black/80 backdrop-blur-md flex items-center justify-center text-white transition-all hover:scale-110 active:scale-95 border border-white/20"
                      onClick={advanceDesktopNext}
                    >
                      <svg className="w-4 h-4 lg:w-5 lg:h-5 2xl:w-8 2xl:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                    </button>
                  </div>
                )}

                {/* Progress Indicators Inside Carousel */}
                {desktopMediaItems.length > 1 && (
                  <div className="absolute bottom-4 lg:bottom-6 2xl:bottom-10 left-0 right-0 z-20 flex justify-center gap-1.5 lg:gap-2 w-full px-4 items-center pointer-events-auto">
                    {desktopMediaItems.map((_, idx) => {
                      const isActive = idx === desktopCurrentIndex;
                      const isPast = idx < desktopCurrentIndex;
                      return (
                        <motion.div
                          key={idx}
                          className="h-1 lg:h-1.5 2xl:h-2.5 rounded-full overflow-hidden bg-white/30 backdrop-blur-md cursor-pointer"
                          initial={false}
                          animate={{
                            width: isActive ? 36 : 14,
                            opacity: isActive ? 1 : isPast ? 0.9 : 0.6
                          }}
                          transition={{ duration: 0.3 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setDesktopCurrentIndex(idx);
                            setProgress(0);
                          }}
                        >
                          {isActive && (
                            <motion.div
                              className="h-full bg-white shadow-[0_0_12px_rgba(255,255,255,1)]"
                              style={{ width: `${progress}%` }}
                              transition={{ duration: 0.1, ease: "linear" }}
                            />
                          )}
                          {isPast && (
                            <div className="w-full h-full bg-white shadow-[0_0_12px_rgba(255,255,255,1)]" />
                          )}
                        </motion.div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
            
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingCategoryHero;
